import {
  QrCode,
  Menu,
  ShoppingCart,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  MessageSquare,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

export function HowItWorksSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      step: 1,
      icon: QrCode,
      title: t("howItWorks.steps.scan.title"),
      description: t("howItWorks.steps.scan.description"),
      color: "bg-blue-500",
    },
    {
      step: 2,
      icon: Menu,
      title: t("howItWorks.steps.browse.title"),
      description: t("howItWorks.steps.browse.description"),
      color: "bg-green-500",
    },
    {
      step: 3,
      icon: ShoppingCart,
      title: t("howItWorks.steps.order.title"),
      description: t("howItWorks.steps.order.description"),
      color: "bg-orange-500",
    },
    {
      step: 4,
      icon: CheckCircle,
      title: t("howItWorks.steps.received.title"),
      description: t("howItWorks.steps.received.description"),
      color: "bg-purple-500",
    },
  ];

  const businessFeatures = [
    {
      icon: Clock,
      title: t("howItWorks.features.analytics.title"),
      description: t("howItWorks.features.analytics.description"),
      highlight: t("howItWorks.features.analytics.highlight"),
      color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
    },
    {
      icon: Users,
      title: "Intelligent Staff Management Hub",
      description:
        "Are you most times short staffed? Our unified hub helps you manage your team efficiently, optimize scheduling, and reduce operational stress",
      highlight: "Never be understaffed again",
      color: "bg-gradient-to-br from-teal-500 to-teal-600",
    },
    {
      icon: BarChart3,
      title: "Advanced Data Analytics & Reports",
      description:
        "Are you lacking proper data analytics and reports of sales and inventory management? Get comprehensive insights, real-time sales tracking, and inventory optimization at your fingertips",
      highlight: "Make data-driven decisions",
      color: "bg-gradient-to-br from-rose-500 to-rose-600",
    },
    {
      icon: MessageSquare,
      title: "Real-Time Staff Communication",
      description:
        "Do you need one hub for managing your staff? Chat with them in real-time even in your absence and maintain seamless operations 24/7",
      highlight: "Stay connected always",
      color: "bg-gradient-to-br from-amber-500 to-amber-600",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2
            className={`text-3xl md:text-4xl font-bold tracking-tight mb-6 transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            {t("howItWorks.title")}
          </h2>
          <p
            className={`text-lg text-muted-foreground transition-all duration-1000 delay-300 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            {t("howItWorks.subtitle")}
          </p>
        </div>

        <div className="relative mb-20">
          {/* Connection Lines */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-green-500 via-orange-500 to-purple-500 opacity-30"></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={step.step} className="relative">
                <Card
                  className={`border-0 shadow-lg hover:shadow-xl transition-all duration-700 hover:translate-y-[-8px] bg-white transform ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-20"
                  }`}
                  style={{
                    transitionDelay: isVisible ? `${index * 0.2}s` : "0s",
                  }}
                >
                  <CardContent className="p-6 text-center">
                    {/* Step Number */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div
                        className={`w-8 h-8 ${step.color} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`}
                      >
                        {step.step}
                      </div>
                    </div>

                    {/* Icon */}
                    <div
                      className={`w-16 h-16 ${step.color} rounded-full flex items-center justify-center mx-auto mb-6 mt-4 shadow-lg`}
                    >
                      <step.icon className="h-8 w-8 text-white" />
                    </div>

                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Business Intelligence Section */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3
              className={`text-2xl md:text-3xl font-bold tracking-tight mb-4 transition-all duration-1000 delay-500 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              {t("howItWorks.transform.title")}
            </h3>
            <p
              className={`text-lg text-gray-600 transition-all duration-1000 delay-700 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              {t("howItWorks.transform.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {businessFeatures.map((feature, index) => (
              <Card
                key={feature.title}
                className={`group border-0 shadow-xl hover:shadow-2xl transition-all duration-700 hover:translate-y-[-8px] bg-white overflow-hidden transform ${
                  isVisible
                    ? "opacity-100 translate-x-0"
                    : "opacity-100 translate-x-0"
                }`}
                style={{
                  transitionDelay: "0s",
                }}
              >
                <CardContent className="p-8 relative">
                  {/* Background pattern */}
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                    <feature.icon className="w-full h-full" />
                  </div>

                  <div className="flex items-start gap-6 relative z-10">
                    <div
                      className={`w-16 h-16 ${feature.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h4 className="text-xl font-bold text-gray-900">
                          {feature.title}
                        </h4>
                        <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full">
                          {feature.highlight}
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>

                  {/* Hover effect gradient */}
                  <div
                    className={`absolute inset-0 ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-lg`}
                  ></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Demo Video/Image Placeholder */}
        <div
          className={`mt-20 transition-all duration-1000 delay-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700">
            <div className="relative h-64 md:h-96 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="text-center relative z-10">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <QrCode className="h-12 w-12 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">
                  {t("howItWorks.demo.title")}
                </h3>
                <p className="text-blue-100 text-lg">
                  {t("howItWorks.demo.subtitle")}
                </p>
              </div>
              {/* Animated background elements */}
              <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
              <div className="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-pulse delay-1000"></div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
